import React from "react";

export function SectionEyebrow({ children, className = "" }) {
  return <div className={`eyebrow ${className}`}>{children}</div>;
}

export function SectionHeading({ eyebrow, title, lead, align = "left" }) {
  const alignment = align === "center" ? "text-center mx-auto" : "text-left";
  return (
    <div className={`max-w-3xl ${alignment}`}>
      {eyebrow && <SectionEyebrow className="mb-4">{eyebrow}</SectionEyebrow>}
      <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05] tracking-tight">
        {title}
      </h2>
      {lead && (
        <p className="mt-6 text-slate-300 text-lg leading-relaxed max-w-2xl">
          {lead}
        </p>
      )}
    </div>
  );
}

export function GoldButton({
  as: Tag = "button",
  children,
  className = "",
  ...rest
}) {
  return (
    <Tag
      {...rest}
      className={`inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#142339] px-7 py-4 text-xs uppercase tracking-[0.22em] font-bold hover:bg-[#FDE047] transition-colors ${className}`}
    >
      {children}
    </Tag>
  );
}

export function GhostGoldButton({
  as: Tag = "button",
  children,
  className = "",
  ...rest
}) {
  return (
    <Tag
      {...rest}
      className={`inline-flex items-center justify-center gap-2 border border-[#D4AF37] text-[#D4AF37] px-7 py-4 text-xs uppercase tracking-[0.22em] font-bold hover:bg-[#D4AF37]/10 transition-colors ${className}`}
    >
      {children}
    </Tag>
  );
}

export function PillarCard({ icon: Icon, title, body, footer, testid }) {
  return (
    <div
      data-testid={testid}
      className="relative bg-[#1A2B45] border border-white/10 p-8 sm:p-10 hover:border-[#D4AF37]/50 transition-colors flex flex-col group"
    >
      <div className="w-12 h-12 border border-[#D4AF37]/40 flex items-center justify-center text-[#D4AF37] mb-6 group-hover:border-[#D4AF37] transition-colors">
        {Icon ? <Icon className="w-6 h-6" /> : null}
      </div>
      <h3 className="font-serif-display text-2xl sm:text-3xl text-white mb-3">
        {title}
      </h3>
      <p className="text-slate-300 leading-relaxed text-sm">{body}</p>
      {footer && (
        <div className="mt-6 pt-6 border-t border-white/10 text-xs uppercase tracking-[0.22em] text-[#D4AF37]">
          {footer}
        </div>
      )}
    </div>
  );
}

export function TrustChip({ icon: Icon, label }) {
  return (
    <div className="flex items-center justify-center gap-2.5 px-4 py-3.5 border border-white/10 bg-white/[0.02] text-center">
      {Icon && <Icon className="w-4 h-4 text-[#D4AF37] shrink-0" />}
      <span className="text-[11px] sm:text-xs uppercase tracking-[0.14em] text-slate-200 leading-tight">
        {label}
      </span>
    </div>
  );
}

export function PageHero({
  eyebrow,
  title,
  lead,
  children,
  backdrop,
  backdropOpacity = 0.25,
  bright = false,
}) {
  return (
    <section className="relative overflow-hidden border-b border-white/10">
      {backdrop && (
        <FadedBackdrop src={backdrop} opacity={backdropOpacity} bright={bright} />
      )}
      <div
        className={`absolute inset-0 grid-bg pointer-events-none ${
          bright ? "opacity-15" : "opacity-40"
        }`}
      />
      <div
        className={`absolute inset-0 pointer-events-none ${
          bright
            ? "bg-gradient-to-b from-transparent via-transparent to-[#142339]/70"
            : "bg-gradient-to-b from-transparent via-transparent to-[#142339]"
        }`}
      />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-24 sm:py-32">
        {eyebrow && <SectionEyebrow className="mb-6">{eyebrow}</SectionEyebrow>}
        <h1
          className={`font-serif-display text-5xl sm:text-6xl lg:text-7xl text-white leading-[1.02] tracking-tighter max-w-4xl ${
            bright
              ? "[text-shadow:0_2px_20px_rgba(11,17,32,0.85),0_1px_4px_rgba(11,17,32,0.9)]"
              : ""
          }`}
        >
          {title}
        </h1>
        {lead && (
          <p
            className={`mt-8 text-lg sm:text-xl text-slate-100 max-w-2xl leading-relaxed ${
              bright
                ? "[text-shadow:0_1px_10px_rgba(11,17,32,0.85)]"
                : ""
            }`}
          >
            {lead}
          </p>
        )}
        {children && <div className="mt-10">{children}</div>}
      </div>
    </section>
  );
}

/**
 * Faded image backdrop for any section.
 * Usage: wrap a section with `relative overflow-hidden` and drop in
 *   <FadedBackdrop src={IMG} opacity={0.18} />
 * Pass `bright` for a lighter vignette that lets more image show through.
 */
export function FadedBackdrop({
  src,
  opacity = 0.2,
  position = "center",
  vignette = true,
  bright = false,
}) {
  const vignetteClass = bright
    ? "bg-gradient-to-b from-[#142339]/20 via-[#142339]/10 to-[#142339]/55"
    : "bg-gradient-to-b from-[#142339]/55 via-[#142339]/45 to-[#142339]/85";
  return (
    <>
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-cover pointer-events-none"
        style={{
          backgroundImage: `url(${src})`,
          backgroundPosition: position,
          opacity,
        }}
      />
      {vignette && (
        <div
          aria-hidden="true"
          className={`absolute inset-0 pointer-events-none ${vignetteClass}`}
        />
      )}
    </>
  );
}
